import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/Button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Activity, 
  Heart, 
  Brain, 
  Users, 
  Calendar, 
  Clock, 
  MessageCircle, 
  Video, 
  Phone,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  FileText,
  Settings,
  Bell,
  Search,
  Filter,
  Download,
  Star,
  Shield,
  Zap,
  Award,
  Target,
  Stethoscope,
  Monitor,
  Tablet,
  Smartphone,
  ArrowRight,
  Plus,
  Edit,
  MoreHorizontal
} from 'lucide-react';
import { Input } from './ui/input';
import { FloatingActionButton } from './FloatingActionButton';



export const MedicalDashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedMetric, setSelectedMetric] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const patientMetrics = [
    {
      id: '1',
      name: 'Active Patients',
      value: 248,
      change: 12,
      status: 'improving',
      icon: Users
    },
    {
      id: '2',
      name: 'Sessions Today',
      value: 18,
      change: -2,
      status: 'stable',
      icon: Video
    },
    {
      id: '3',
      name: 'Emergency Alerts',
      value: 3,
      change: 1,
      status: 'concerning',
      icon: AlertCircle
    },
    {
      id: '4',
      name: 'Completion Rate',
      value: 94,
      change: 3,
      status: 'improving',
      icon: Target
    }
  ];

  const todayAppointments = [
    {
      id: '1',
      patientName: 'Sarah Johnson',
      time: '09:00',
      type: 'video',
      status: 'confirmed',
      urgency: 'medium'
    },
    {
      id: '2',
      patientName: 'Michael Chen',
      time: '10:30',
      type: 'phone',
      status: 'pending',
      urgency: 'low'
    },
    {
      id: '3',
      patientName: 'Emma Williams',
      time: '14:00',
      type: 'in-person',
      status: 'confirmed',
      urgency: 'high'
    },
    {
      id: '4',
      patientName: 'Robert Davis',
      time: '15:30',
      type: 'video',
      status: 'completed',
      urgency: 'low'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'improving':
        return 'text-green-600 bg-green-100';
      case 'stable':
        return 'text-blue-600 bg-blue-100';
      case 'concerning':
        return 'text-amber-600 bg-amber-100';
      case 'critical':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high':
        return 'border-red-500 bg-red-50';
      case 'medium':
        return 'border-amber-500 bg-amber-50';
      default:
        return 'border-green-500 bg-green-50';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return Video;
      case 'phone':
        return Phone;
      default:
        return Users;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-clinic-gray-50 to-clinic-blue-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Medical Dashboard
            </h1>
            <p className="text-xl text-gray-600">
              {currentTime.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search patients..."
                className="input-medical pl-10 w-80"
              />
            </div>
            
            <Button className="icon-container icon-container-md icon-medical">
              <Bell className="icon-md" />
            </Button>
            
            <Button className="icon-container icon-container-md icon-info">
              <Settings className="icon-md" />
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {patientMetrics.map((metric) => (
            <Card 
              key={metric.id}
              className={`card-interactive cursor-pointer transition-all duration-300 ${
                selectedMetric === metric.id ? 'ring-2 ring-clinic-blue-500' : ''
              }`}
              onClick={() => setSelectedMetric(selectedMetric === metric.id ? null : metric.id)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`icon-container icon-container-lg ${getStatusColor(metric.status)} rounded-2xl`}>
                    <metric.icon className="icon-lg" />
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900">
                      {metric.name.includes('Rate') ? `${metric.value}%` : metric.value}
                    </div>
                    <div className={`text-sm flex items-center ${
                      metric.change > 0 ? 'text-green-600' : metric.change < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {metric.change > 0 ? (
                        <TrendingUp className="w-4 h-4 mr-1" />
                      ) : metric.change < 0 ? (
                        <TrendingDown className="w-4 h-4 mr-1" />
                      ) : null}
                      {Math.abs(metric.change)} from yesterday
                    </div>
                  </div>
                </div>
                
                <h3 className="font-medium text-gray-900 mb-2">{metric.name}</h3>
                
                {metric.name.includes('Rate') && (
                  <div className="progress-bar_web progress-bar_web-lg mt-3">
                    <div 
                      className={`progress-bar_web-fill ${
                        metric.status === 'improving' ? 'bg-green-600' :
                        metric.status === 'concerning' ? 'bg-amber-500' :
                        'bg-clinic-blue-600'
                      }`}
                      style={{ width: `${metric.value}%` }}
                    ></div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Today's Appointments */}
          <Card className="lg:col-span-2 card-elevated">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 flex items-center">
                  <Calendar className="w-6 h-6 mr-3 text-clinic-blue-600" />
                  Today's Appointments
                </h3>
                <p className="text-gray-600 mt-1">
                  {todayAppointments.filter(a => a.status !== 'completed').length} remaining
                </p>
              </div>
              <div className="flex space-x-2">
                <Button className="btn-ghost">
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                </Button>
                <Button className="bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Appointment
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {todayAppointments.map((appointment) => {
                const TypeIcon = getTypeIcon(appointment.type);
                return (
                  <div 
                    key={appointment.id} 
                    className={`p-4 rounded-xl border-l-4 transition-all duration-200 hover:shadow-md ${getUrgencyColor(appointment.urgency)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`icon-container icon-container-md ${
                          appointment.type === 'video' ? 'icon-medical' :
                          appointment.type === 'phone' ? 'icon-success' :
                          'icon-info'
                        }`}>
                          <TypeIcon className="icon-md" />
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-gray-900">{appointment.patientName}</h4>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-sm text-gray-600 flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {appointment.time}
                            </span>
                            <Badge 
                              className={`text-xs ${
                                appointment.status === 'confirmed' ? 'badge-success' :
                                appointment.status === 'pending' ? 'badge-warning' :
                                'badge-medical'
                              }`}
                            >
                              {appointment.status}
                            </Badge>
                            <Badge className={`text-xs ${
                              appointment.urgency === 'high' ? 'badge-emergency' :
                              appointment.urgency === 'medium' ? 'badge-warning' :
                              'badge-success'
                            }`}>
                              {appointment.urgency} priority
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {appointment.status !== 'completed' && (
                          <>
                            <Button className="btn-xs bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white">
                              <Video className="w-3 h-3 mr-1" />
                              Join
                            </Button>
                            <Button className="btn-xs btn-ghost">
                              <MessageCircle className="w-3 h-3 mr-1" />
                              Chat
                            </Button>
                          </>
                        )}
                        <Button className="btn-xs btn-ghost">
                          <MoreHorizontal className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Quick Actions & Notifications */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="card-elevated">
              <CardHeader>
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-clinic-blue-600" />
                  Quick Actions
                </h3>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full btn-lg bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white justify-start">
                  <Video className="w-5 h-5 mr-3" />
                  Start Video Session
                </Button>
                
                <Button className="w-full btn-lg bg-green-600 hover:bg-green-700 text-white justify-start">
                  <Calendar className="w-5 h-5 mr-3" />
                  Schedule Appointment
                </Button>
                
                <Button className="w-full btn-lg btn-warning justify-start">
                  <AlertCircle className="w-5 h-5 mr-3" />
                  Emergency Protocol
                </Button>
                
                <Button className="w-full btn-lg btn-ghost justify-start">
                  <FileText className="w-5 h-5 mr-3" />
                  Patient Records
                </Button>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="card-elevated">
              <CardHeader>
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-clinic-blue-600" />
                  Recent Activity
                </h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 p-3 bg-clinic-blue-50 rounded-lg">
                    <div className="icon-container icon-container-sm icon-success">
                      <CheckCircle className="icon-sm" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Session completed</p>
                      <p className="text-xs text-gray-600">Emma Williams • 2 min ago</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-amber-50 rounded-lg">
                    <div className="icon-container icon-container-sm icon-warning">
                      <MessageCircle className="icon-sm" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">New message received</p>
                      <p className="text-xs text-gray-600">Sarah Johnson • 5 min ago</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                    <div className="icon-container icon-container-sm icon-medical">
                      <Heart className="icon-sm" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Vitals updated</p>
                      <p className="text-xs text-gray-600">Michael Chen • 15 min ago</p>
                    </div>
                  </div>
                </div>
                
                <Button className="w-full btn-ghost text-center">
                  View All Activity
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* System Status */}
            <Card className="card-glass">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-900 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-clinic-blue-600" />
                    System Status
                  </h4>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-green-600 font-medium">All Systems Operational</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 flex items-center">
                      <Video className="w-4 h-4 mr-2" />
                      Video Services
                    </span>
                    <Badge className="badge-success text-xs">Online</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 flex items-center">
                      <Shield className="w-4 h-4 mr-2" />
                      Security
                    </span>
                    <Badge className="badge-success text-xs">Secure</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 flex items-center">
                      <Activity className="w-4 h-4 mr-2" />
                      Database
                    </span>
                    <Badge className="badge-success text-xs">Healthy</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Floating Action Button Component */}
      <FloatingActionButton />
    </div>
  );
};